import React from 'react';

import { makeStyles } from '@mui/styles';
import { CardContent, Grid, Paper, Typography } from '@mui/material';
import ParamTable from './ParamTable';
import { CodeHighlighter } from './index';

const useStyles = makeStyles((theme) => ({
  container: {
    margin: 10,
  },
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    '& p': {
      marginTop: 10,
    },
  },
  title: {
    fontSize: '24px !important',
    textAlign: 'left',
    margin: 'auto',
  },
  pre: {
    whiteSpace: 'pre-wrap',
  },
}));

const ChooseIntegration = () => {
  const classes = useStyles();
  return (
    <Grid
      item
      xs={12}
    >
      <a id={'choose_integration'} />
      <Paper className={classes.patientChartCard}>
        <CardContent>
          <Typography
            variant={'h1'}
            className={classes.title}
          >
            Choose Integration
          </Typography>
          <p>
            To handle user interactions, you can point the user to a white-labeled Diet ID page, or embed Diet ID within
            your app.
          </p>
          <h3>2.1 Embedded</h3>
          <p>
            If you have an app or a website and you want Diet ID’s modules accessible there, you can embed Diet ID’s
            modules with a simple iframe. To do this simply add an iframe tag with the src attribute set to your custom
            url.
          </p>
          <p>
            Users will still need to create accounts or sign in to use the tools, but the experience will take place
            within your app or digital platform, and therefore appear more seamless. Once the user completes the
            assessment, we can redirect them to any page you like.
          </p>
          <p>
            This method is perfect if you have a website or landing page where you drive all of your users, and you want
            Diet ID’s modules to “live” there, but you have limited tech resources.
          </p>
          <p>Set up for this usually takes one day.</p>
          <h3>2.2 Embedded with SSO</h3>
          <p>
            If you want your users to use Diet ID without having to create accounts, we can automatically create
            accounts for them via SSO. This allows for seamless switching between your app and Diet ID, and ensures a
            one-to-one mapping between your user accounts and corresponding Diet ID accounts.
          </p>
          <p style={{ display: 'none' }}>
            Getting this running will require some backend work, as you&#39;ll be generating SSO urls using a secret key
            shared between your backend and Diet ID&#39;s backend. See <code>getWorkflowUrl</code> in our{' '}
            <a
              href="https://api-prod.dietid.com/api/v1/docs/example/api_client"
              rel="noreferrer"
              target={'_blank'}
            >
              example client
            </a>{' '}
            for how to generate the SSO url server-side.
          </p>
          <p>
            Once the url is generated and sent to the frontend, simply set the <code>src</code> attribute of an iframe
            to the SSO url and you&#39;ll be good to go. Users who haven&#39;t completed an assessment will see the
            assessment, while existing users will see their results. Accounts are automatically deduped based on the
            user id you pass to Diet ID.
          </p>
          <h3>2.3 Embedded + Window Message Events</h3>
          <p>
            Don&#39;t want to wait for the webhook? Want to track each step of the Assessment Workflow? You can achieve
            that via our Event Messages.
          </p>
          <p>
            Each step the user takes is shared with you in real-time and can be utilized via standard event listeners:
          </p>
          <p>Steps: &apos;Screeners&apos; | &apos;ID&apos; | &apos;Ideal&apos; | &apos;Navigation&apos;</p>
          <p>
            <CodeHighlighter
              code={`useEffect(() => {
    const onReceiveMessage = ({ origin, data }: MessageEvent) => {
      if (origin.includes('.dietid.com') && data === 'dietid:assessmentComplete') {
        // You can monitor incoming messages and choose when to close, reload or any other action
        loadUserAndLatestWorkflow() || closeAssessment()
      }
    }

    // enable cross-frame communication with Diet ID
    window.addEventListener('message', onReceiveMessage, false)

    return () => {
      window.removeEventListener('message', onReceiveMessage)
    }
  }, [])`}
            />
            <p>Example Message:</p>
            <CodeHighlighter
              code={`{
	"source": "DietID",
	"eventName": "workflowprogresschange",
	"payload": {
		"section": "ID",
		"percentComplete": 1,
		"completedSteps": 8,
		"totalSteps": 8,
		"workflowID": 3680
	}
}`}
            />
          </p>
          <h3>2.4 API</h3>
          <p>
            If you want to pull data as needed, we have a public API available! Please see the{' '}
            <a
              href="#api_endpoints"
              rel="nofollow"
            >
              API Endpoint
            </a>{' '}
            section for further information on the various endpoints.
          </p>
          <h3>API Environments:</h3>
          <ParamTable
            rows={[
              { name: 'Staging API', type: 'https://api-staging.dietid.com/api/:version/:endpoint' },
              { name: 'Production API', type: 'https://api-prod.dietid.com/api/:version/:endpoint' },
            ]}
          />

          <h3>API Authenticate-As</h3>
          <p>
            Some endpoints allow you to authenticate as a Partner by providing your clientSecret as a bearer token.
            However, it is recommended to authenticate on behalf of the Patient using their accessToken.
            <br />
            <i>
              * This approach ensures that the returned data is always adjusted and helps distribute your API
              Rate-Limit.
            </i>
          </p>
          <ul>
            <li>
              <p>
                To achieve this, include the <code>x-dqpn-partner</code> header (Partner&apos;s clientSecret) and either
                the <code>x-dqpn-user-id</code> or <code>x-dqpn-partner-user-id</code> header with a matching value.
              </p>
            </li>
            <li>
              <p>
                To authenticate as a Patient without their accessToken, use the Partner&apos;s clientSecret as a bearer
                token and include either the <code>x-dqpn-user-id</code> or <code>x-dqpn-partner-user-id</code> header
                with a matching value.
              </p>
            </li>
          </ul>

          <h3>API Rate-Limits</h3>
          <ul>
            <li>10 queries per second (QPS) per IP address.</li>
            <li>
              Rate limit of 1,000 requests per hour and per user. Diet ID reserves the right to enforce API
              Rate-Limit&#39;s at our discretion.
            </li>
            <li>Service may be affected if you are over limit for 24 consecutive hours.</li>
          </ul>
          <Grid
            container
            item
            xs={12}
            spacing={2}
            justifyContent={'space-between'}
          >
            <Grid
              item
              xs={2}
            >
              <h3>Rate-Limit Error Response</h3>
            </Grid>
            <Grid
              item
              xs={10}
            >
              <pre className={classes.pre}>
                <code>&#123; &quot;error&quot;: &quot;Throttle limit reached. Retry later.&quot; &#125;</code>
              </pre>
            </Grid>
          </Grid>
        </CardContent>
      </Paper>
    </Grid>
  );
};
export default ChooseIntegration;
